import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  RefreshCw, Play, Landmark, Wallet, CreditCard, TrendingUp, FileText, Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  MATCH:          { label: 'Sudah Cocok',       color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  PENDING_DB:     { label: 'Belum Terkonfirmasi', color: 'bg-amber-50 text-amber-700 border-amber-200',      icon: AlertTriangle },
  DUPLICATE_CASH: { label: 'Kemungkinan Duplikat', color: 'bg-rose-50 text-rose-700 border-rose-200',        icon: XCircle },
  MISSING:        { label: 'Tidak Ditemukan',   color: 'bg-slate-100 text-slate-600 border-slate-300',      icon: HelpCircle },
};

const ACTION_LABEL = {
  none: 'Sudah OK',
  mark_completed: 'Tandai Lunas',
  refund_cash_complete_duitku: 'Refund Cash + Lunas Duitku',
  skip: 'Lewati',
  review: 'Butuh Review',
};

const StatCard = ({ icon: Icon, label, value, tint, testId }) => (
  <Card data-testid={testId} className={`p-5 border-0 shadow-sm ${tint}`}>
    <div className="flex items-center justify-between">
      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-2xl font-bold tabular-nums">{value.toLocaleString('id-ID')}</div>
      </div>
      <div className="w-11 h-11 rounded-xl bg-white/60 flex items-center justify-center backdrop-blur-sm">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

const Reconciliation = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [selected, setSelected] = useState({});
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files[0]) setFile(files[0]);
  }, []);

  const handleUpload = async () => {
    if (!file) { toast.error('Pilih file dulu'); return; }
    if (!/\.xlsx?$/i.test(file.name)) { toast.error('File harus Excel (.xlsx / .xls)'); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/v2/duitku/reconcile/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        setResult(res.data);
        // Pre-select all rows with actionable suggested_action
        const pre = {};
        (res.data.rows || []).forEach((r) => {
          if (r.suggested_action === 'mark_completed' || r.suggested_action === 'refund_cash_complete_duitku') {
            pre[r.matched_payment?.payment_id] = true;
          }
        });
        setSelected(pre);
        toast.success(`Berhasil parse ${res.data.summary.total} baris`);
      } else {
        toast.error(res.data?.message || 'Upload gagal');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload gagal');
    } finally {
      setUploading(false);
    }
  };

  const handleExecute = async () => {
    if (!result) return;
    const actions = result.rows
      .filter((r) => r.matched_payment?.payment_id && selected[r.matched_payment.payment_id])
      .map((r) => ({
        payment_id: r.matched_payment.payment_id,
        action: r.suggested_action,
        cash_payment_id: r.cash_payment_id || undefined,
      }));
    if (actions.length === 0) { toast.warning('Tidak ada baris yang dipilih'); return; }
    if (!window.confirm(`Yakin proses ${actions.length} rekonsiliasi? Backup DB akan dibuat otomatis.`)) return;
    setExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/v2/duitku/reconcile/execute`, { actions }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        toast.success(`Selesai: ${res.data.succeeded}/${res.data.processed} sukses`);
        // Re-upload for fresh preview
        handleUpload();
      } else {
        toast.error('Eksekusi gagal');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eksekusi gagal');
    } finally {
      setExecuting(false);
    }
  };

  const toggleAll = (val) => {
    if (!result) return;
    const next = {};
    result.rows.forEach((r) => {
      if (val && r.matched_payment?.payment_id &&
          (r.suggested_action === 'mark_completed' || r.suggested_action === 'refund_cash_complete_duitku')) {
        next[r.matched_payment.payment_id] = true;
      }
    });
    setSelected(next);
  };

  const totalToProcess = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 animate-in fade-in duration-300" data-testid="reconciliation-page">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2">
        <div className="flex items-start gap-4">
          <div className="pt-1">
            <Landmark className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
              Rekonsiliasi Bank / Duitku
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl leading-relaxed">
              Upload merchant report Excel dari Duitku. Sistem akan cross-check dengan payment di DB, deteksi duplikat cash, dan rekonsiliasi otomatis dalam 1 klik.
            </p>
          </div>
        </div>
      </div>

      {/* UPLOAD PANEL */}
      <Card className="p-6 shadow-sm border-slate-200 dark:border-slate-700/60">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
            dragOver ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/40'
          }`}
          onClick={() => document.getElementById('reconcile-file-input')?.click()}
          data-testid="reconcile-dropzone"
        >
          <input
            id="reconcile-file-input"
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0])}
            data-testid="reconcile-file-input"
          />
          <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {file ? file.name : 'Drop file Excel Duitku di sini atau klik untuk pilih'}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Format: .xlsx atau .xls (dari hPanel Duitku → Merchant Report)
          </div>
          {file && (
            <div className="text-[11px] text-slate-500 mt-2">
              Size: {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          {file && (
            <Button variant="ghost" onClick={() => { setFile(null); setResult(null); setSelected({}); }} data-testid="reconcile-clear">
              Batal
            </Button>
          )}
          <Button onClick={handleUpload} disabled={!file || uploading} data-testid="reconcile-upload-btn">
            {uploading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Menganalisis...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload & Analisis</>
            )}
          </Button>
        </div>
      </Card>

      {/* PREVIEW SECTION */}
      {result && (
        <>
          {/* SUMMARY CARDS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              testId="stat-total"
              icon={FileText}
              label="Total Baris Duitku"
              value={result.summary.total}
              tint="bg-gradient-to-br from-slate-50 to-slate-100 text-slate-700"
            />
            <StatCard
              testId="stat-match"
              icon={CheckCircle2}
              label="Sudah Cocok"
              value={result.summary.match}
              tint="bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-700"
            />
            <StatCard
              testId="stat-pending"
              icon={AlertTriangle}
              label="Perlu Konfirmasi"
              value={result.summary.pending_db + result.summary.duplicate_cash}
              tint="bg-gradient-to-br from-amber-50 to-orange-50 text-amber-700"
            />
            <StatCard
              testId="stat-missing"
              icon={HelpCircle}
              label="Tidak Ditemukan"
              value={result.summary.missing}
              tint="bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600"
            />
          </div>

          {/* BULK ACTIONS */}
          {(result.summary.pending_db + result.summary.duplicate_cash) > 0 && (
            <Card className="p-4 shadow-sm border-blue-200 dark:border-blue-800/60 bg-blue-50/40 dark:bg-blue-950/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900 dark:text-blue-200">
                    <div className="font-medium">Ada {result.summary.pending_db + result.summary.duplicate_cash} baris yang bisa direkonsiliasi</div>
                    <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Pilih baris yang ingin diproses, lalu klik "Eksekusi". Backup DB akan dibuat otomatis sebelum perubahan.
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleAll(true)} data-testid="reconcile-select-all">Pilih Semua</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleAll(false)} data-testid="reconcile-unselect-all">Batal Semua</Button>
                  <Button size="sm" onClick={handleExecute} disabled={executing || totalToProcess === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="reconcile-execute-btn">
                    {executing ? (
                      <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Memproses...</>
                    ) : (
                      <><Play className="w-3.5 h-3.5 mr-1.5" /> Eksekusi ({totalToProcess})</>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* RESULTS TABLE */}
          <Card className="overflow-hidden shadow-sm border-slate-200 dark:border-slate-700/60">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700/60">
                  <tr>
                    <th className="text-left py-3 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-8">Pilih</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Publisher ID</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Channel</th>
                    <th className="text-right py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Jumlah</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Tanggal</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Pelanggan / Invoice</th>
                    <th className="text-center py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Aksi Disarankan</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, idx) => {
                    const meta = STATUS_META[r.status] || STATUS_META.MISSING;
                    const Icon = meta.icon;
                    const canSelect = r.matched_payment?.payment_id && (r.suggested_action === 'mark_completed' || r.suggested_action === 'refund_cash_complete_duitku');
                    return (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-blue-50/30 dark:hover:bg-slate-800/40 transition-colors duration-150" data-testid={`reconcile-row-${idx}`}>
                        <td className="py-3 px-3">
                          {canSelect ? (
                            <input
                              type="checkbox"
                              checked={!!selected[r.matched_payment.payment_id]}
                              onChange={(e) => setSelected({ ...selected, [r.matched_payment.payment_id]: e.target.checked })}
                              className="w-4 h-4 cursor-pointer accent-blue-600"
                              data-testid={`reconcile-check-${idx}`}
                            />
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs font-mono text-slate-700 dark:text-slate-300 truncate max-w-[160px]" title={r.publisher_order_id}>
                            {r.publisher_order_id}
                          </div>
                          {r.matched_payment?.payment_gateway_ref && (
                            <div className="text-[10px] font-mono text-slate-400 mt-0.5 truncate max-w-[160px]" title={r.matched_payment.payment_gateway_ref}>
                              → {r.matched_payment.payment_gateway_ref}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                            <Wallet className="w-3 h-3 opacity-60" /> {r.channel_hint || '?'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-medium text-slate-800 dark:text-slate-100 tabular-nums whitespace-nowrap">
                          Rp {r.amount.toLocaleString('id-ID')}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {r.trans_date ? format(new Date(r.trans_date), 'dd MMM yyyy', { locale: idLocale }) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          {r.matched_payment ? (
                            <div>
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{r.matched_payment.customer_name}</div>
                              <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{r.matched_payment.invoice_number}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center">
                            <Badge className={`${meta.color} text-[11px] py-1 px-2.5 inline-flex items-center gap-1.5 font-medium border`}>
                              <Icon className="w-3 h-3" /> {meta.label}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-300">
                          {ACTION_LABEL[r.suggested_action] || r.suggested_action}
                          {r.cash_amount && (
                            <div className="text-[10px] text-rose-600 mt-0.5">
                              Refund cash: Rp {r.cash_amount.toLocaleString('id-ID')}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default Reconciliation;
